import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { waLink, WA_DEFAULT_MSG } from "@/lib/wa";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { WhatsAppIcon, LangPill } from "./Shared";
import { ThemeToggle } from "./ThemeToggle";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { lang } = useLang();
    const t = STR.nav[lang];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
                scrolled || open ? "glass shadow-lg shadow-black/30" : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
                <a href="/#beranda" data-testid="nav-logo" className="flex items-center gap-2">
                    <img
                        src="/assets/techflaze-logo.webp"
                        alt="Logo TechFlaze"
                        className="h-9 w-auto md:h-11"
                        width="160"
                        height="44"
                    />
                </a>

                <ul className="hidden items-center gap-6 lg:flex">
                    {t.links.map((l) => (
                        <li key={l.id}>
                            <a
                                href={l.href}
                                data-testid={`nav-link-${l.id}`}
                                className="nav-link text-sm font-medium text-zinc-300 transition-colors duration-200 hover:text-amber-400"
                            >
                                {l.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3">
                    <ThemeToggle className="hidden sm:flex" />
                    <LangPill className="hidden sm:flex" />
                    <a
                        href={waLink(WA_DEFAULT_MSG)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-wa-button"
                        className="btn-flame hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-transform duration-200 hover:scale-105 sm:inline-flex"
                    >
                        <WhatsAppIcon className="h-4 w-4" />
                        {t.cta}
                    </a>
                    <button
                        type="button"
                        aria-label={open ? "Tutup menu" : "Buka menu"}
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="rounded-lg border border-white/10 p-2 text-zinc-200 lg:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </nav>

            {open && (
                <div data-testid="nav-mobile-menu" className="glass border-t border-white/10 lg:hidden">
                    <ul className="space-y-1 px-5 py-4">
                        {t.links.map((l) => (
                            <li key={l.id}>
                                <a
                                    href={l.href}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => setOpen(false)}
                                    className="block rounded-lg px-3 py-3 text-base font-medium text-zinc-200 transition-colors duration-200 hover:bg-white/5 hover:text-amber-400"
                                >
                                    {l.label}
                                </a>
                            </li>
                        ))}
                        <li className="flex items-center gap-3 px-3 pt-3">
                            <LangPill />
                            <ThemeToggle />
                        </li>
                        <li className="pt-2">
                            <a
                                href={waLink(WA_DEFAULT_MSG)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-wa-button"
                                className="btn-flame flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white"
                            >
                                <WhatsAppIcon className="h-4 w-4" />
                                {t.cta}
                            </a>
                        </li>
                    </ul>
                </div>
            )}
        </header>
    );
};
